import React from "react";
import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  compactSwitchSx,
  workspaceSwitchSx,
} from "../../../../Dashboard/overviewPanel";
import { hapticSelection } from "../../../../../lib/haptics";

export type ViewMode = "quarterly" | "annual";

export interface ToggleOption {
  value: string;
  label: string;
}

export interface ChartHeaderProps {
  /** Chart title */
  title: string;
  /** Whether to show the header (can be hidden for embedded charts) */
  show?: boolean;
  /** Toggle options for period selection */
  toggleOptions?: ToggleOption[];
  /** Current toggle value */
  toggleValue?: string;
  /** Callback when toggle changes */
  onToggleChange?: (value: string) => void;
  /** Whether to show fullscreen button */
  showFullscreen?: boolean;
  /** Whether currently in modal mode */
  isModal?: boolean;
  /** Callback to open fullscreen modal */
  onOpenModal?: () => void;
  /** Callback to close modal */
  onCloseModal?: () => void;
  /** Additional actions to render in the header */
  actions?: React.ReactNode;
  /** Content rendered centred between the title and the right-hand controls */
  centerContent?: React.ReactNode;
  /** Title placement: left (default) or dead centre with the controls on the right */
  titleAlign?: "left" | "center";
  /** Title colour; defaults to the brand green */
  titleColor?: string;
}

/**
 * Generic header for financial charts with:
 * - Title
 * - Optional period toggle (quarterly/annual or custom)
 * - Optional fullscreen button
 * - Optional close button (in modal mode)
 */
export const ChartHeader: React.FC<ChartHeaderProps> = ({
  title,
  show = true,
  toggleOptions,
  toggleValue,
  onToggleChange,
  showFullscreen = false,
  isModal = false,
  onOpenModal,
  onCloseModal,
  actions,
  centerContent,
  titleAlign = "left",
  titleColor = "primary.main",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // A centred title shares its row with the controls in equal side columns;
  // on a phone the toggle is wider than its column and runs over the title,
  // so phones keep the title on the left.
  const centred = titleAlign === "center" && !isMobile;

  if (!show) {
    return null;
  }

  const handleToggleChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null && newValue !== toggleValue && onToggleChange) {
      hapticSelection();
      onToggleChange(newValue);
    }
  };

  return (
    <Box mb={1}>
      <Box
        sx={{
          display: centred ? "grid" : "flex",
          gridTemplateColumns: centred ? "1fr auto 1fr" : undefined,
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            gridColumn: centred ? 2 : undefined,
            flex: centred ? undefined : "1 1 auto",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="subtitle1"
            component="h2"
            noWrap
            sx={{
              fontWeight: 600,
              color: titleColor,
              fontSize: isModal ? "1.25rem" : "1rem",
            }}
          >
            {title}
          </Typography>
        </Box>

        {centerContent && (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {centerContent}
          </Box>
        )}

        <Box
          sx={{
            gridColumn: centred ? 3 : undefined,
            justifySelf: centred ? "end" : undefined,
            flex: "0 1 auto",
            flexWrap: "wrap",
            minWidth: 0,
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {/* Period Toggle */}
          {toggleOptions && toggleOptions.length > 0 && (
            <ToggleButtonGroup
              value={toggleValue}
              exclusive
              onChange={handleToggleChange}
              size="small"
              aria-label={`${title} view`}
              sx={[workspaceSwitchSx, isMobile && compactSwitchSx]}
            >
              {toggleOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {/* Custom actions */}
          {actions}

          {/* Fullscreen Button */}
          {showFullscreen && !isModal && !isMobile && (
            <IconButton
              aria-label={`Expand ${title} chart`}
              onClick={onOpenModal}
              size="small"
              sx={{
                color: "primary.main",
              }}
            >
              <FullscreenIcon />
            </IconButton>
          )}

          {/* Close Button (Modal mode) */}
          {isModal && (
            <IconButton
              aria-label={`Close ${title} chart`}
              onClick={onCloseModal}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/** Common toggle options for quarterly/annual views */
export const PERIOD_TOGGLE_OPTIONS: ToggleOption[] = [
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

export default ChartHeader;
